import { Link } from 'react-router-dom';
import { CheckCircle2, Shield, Clock, Award, ArrowRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const FacadeReform = () => {
  const benefits = [
    {
      icon: CheckCircle2,
      title: 'Valorização do Imóvel',
      description: 'Uma fachada bem cuidada pode valorizar o imóvel em até 30%, além de atrair novos moradores e compradores.'
    },
    {
      icon: Shield,
      title: 'Proteção Estrutural',
      description: 'Previne infiltrações, corrosão de ferragens e outros problemas que podem comprometer a estrutura do edifício.'
    },
    {
      icon: Clock,
      title: 'Economia a Longo Prazo',
      description: 'Manutenção preventiva é muito mais econômica que reparos emergenciais de danos estruturais.'
    },
    {
      icon: Award,
      title: 'Modernização Estética',
      description: 'Renova a aparência do edifício, tornando-o mais atraente e alinhado com tendências contemporâneas.'
    }
  ];

  const faqs = [
    {
      question: 'Quanto tempo dura uma reforma de fachada?',
      answer: 'O prazo varia conforme o tamanho e complexidade do projeto. Edifícios residenciais de médio porte levam entre 3 a 6 meses. Fornecemos cronograma detalhado após vistoria técnica.'
    },
    {
      question: 'Qual a frequência ideal para reforma de fachada?',
      answer: 'Recomenda-se realizar manutenção preventiva a cada 5-8 anos. Porém, isso varia conforme exposição a intempéries, qualidade dos materiais e manutenção regular.'
    },
    {
      question: 'A obra vai atrapalhar os moradores?',
      answer: 'Trabalhamos para minimizar transtornos. Executamos em horário comercial, com isolamento adequado, proteção contra poeira e ruídos, e comunicação constante com moradores.'
    },
    {
      question: 'Quais materiais são utilizados?',
      answer: 'Utilizamos apenas materiais de primeira linha com garantia de fabricante: tintas Suvinil/Coral, massas Quartzolit/Vedacit, impermeabilizantes Sika/Viapol, entre outros.'
    },
    {
      question: 'É necessário aprovar em assembleia?',
      answer: 'Sim. Reformas de fachada são obras extraordinárias que necessitam aprovação em assembleia. Auxiliamos com material de apresentação e esclarecimentos técnicos.'
    },
    {
      question: 'Há garantia dos serviços?',
      answer: 'Sim. Oferecemos garantia de 5 anos para serviços estruturais e impermeabilização, e 2 anos para pintura, conforme estabelecido pela NBR 15.575.'
    }
  ];

  return (
    <div className="mt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 text-white py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-[#F47A20] hover:bg-[#e66a10] text-white">
              Serviço em Destaque
            </Badge>
            <h1 className="text-5xl font-bold">
              Reforma de Fachada: Nossa Especialidade
            </h1>
            <p className="text-xl text-blue-100">
              Especialistas em revitalização completa de fachadas com tecnologia avançada, segurança total e resultados que superam expectativas.
            </p>
            <Link to="/contato">
              <Button size="lg" className="bg-gradient-to-r from-[#F47A20] to-[#e66a10] hover:from-[#e66a10] hover:to-[#d45d09] text-white shadow-xl">
                Solicitar Vistoria Gratuita
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#15448A]/10 text-[#15448A] hover:bg-[#15448A]/20 mb-4">
              Serviços Inclusos
            </Badge>
            <h2 className="text-4xl font-bold mb-4">O Que Está Incluído na Reforma de Fachada</h2>
            <p className="text-lg text-gray-600">
              Um processo completo e abrangente para garantir durabilidade e excelência estética.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Inspeção Técnica Detalhada',
                items: ['Análise estrutural completa', 'Identificação de patologias', 'Laudo técnico fotográfico', 'Recomendações de intervenção']
              },
              {
                title: 'Tratamento de Superfície',
                items: ['Limpeza de alta pressão', 'Remoção de material solto', 'Tratamento de trincas e fissuras', 'Preparação do substrato']
              },
              {
                title: 'Reparos Estruturais',
                items: ['Tratamento de ferragens expostas', 'Recuperação de concreto', 'Rejuntamento de pastilhas', 'Reparo de elementos decorativos']
              },
              {
                title: 'Impermeabilização Completa',
                items: ['Sistema de impermeabilização flexível', 'Tratamento de juntas de dilatação', 'Proteção de marquises', 'Aplicação de hidrofugante']
              },
              {
                title: 'Pintura de Alta Qualidade',
                items: ['Tintas premium com garantia', 'Sistema completo de pintura', 'Acabamento impecável', 'Proteção UV e antimofo']
              },
              {
                title: 'Segurança e Documentação',
                items: ['Cumprimento de NR-18 e NR-35', 'Seguro de responsabilidade civil', 'ART de todos os serviços', 'Garantia estendida por escrito']
              }
            ].map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-lg font-bold text-gray-900">{service.title}</h3>
                  <ul className="space-y-2">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm text-gray-600">
                        <span className="text-[#15448A] mt-0.5">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-orange-100 text-[#e66a10] hover:bg-orange-200 mb-4">
              Benefícios
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Por Que Investir na Reforma de Fachada</h2>
            <p className="text-lg text-gray-600">
              Uma fachada bem cuidada traz benefícios que vão muito além da estética.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="flex space-x-6 p-6 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#15448A] to-[#0d2f5e] flex items-center justify-center text-white">
                      <Icon size={28} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Before/After Image Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-[#15448A]/10 text-[#15448A] hover:bg-[#15448A]/20 mb-4">
              Resultados
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Transformações Incríveis</h2>
            <p className="text-lg text-gray-600">
              Veja como nossos projetos transformam edifícios e agregam valor ao patrimônio.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <Card className="overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="relative h-[500px]">
                <img
                  src="/gallery/work-7.jpg"
                  alt="Condomínio Revitalizado - LUVCORP"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-[#15448A] text-white shadow-lg">
                    Reforma Completa
                  </Badge>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-white text-xl font-bold mb-2">Condomínio Revitalizado</h3>
                  <p className="text-white/90 text-sm">Fachada completamente renovada com tinta emborrachada e acabamento premium</p>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="relative h-[500px]">
                <img
                  src="/gallery/work-12.jpg"
                  alt="Reforma Fachada Pastilhas - LUVCORP"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-[#15448A] text-white shadow-lg">
                    Reforma de Fachada
                  </Badge>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-white text-xl font-bold mb-2">Reforma Fachada Pastilhas</h3>
                  <p className="text-white/90 text-sm">Revitalização completa com teste de percussão e sistema de ancoragem seguro</p>
                </div>
              </div>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/projetos">
              <Button size="lg" className="bg-gradient-to-r from-[#15448A] to-[#0d2f5e] hover:from-[#0d2f5e] hover:to-[#15448A] text-white shadow-lg">
                Ver Mais Projetos
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="bg-orange-100 text-[#e66a10] hover:bg-orange-200 mb-4">
              Dúvidas Frequentes
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Perguntas Comuns sobre Reforma de Fachada</h2>
            <p className="text-lg text-gray-600">
              Respondemos as principais dúvidas de síndicos e gestores condominiais.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-white border-2 rounded-xl px-6">
                  <AccordionTrigger className="text-lg font-semibold hover:text-[#15448A] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pt-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-[#F47A20] to-[#e66a10] text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">
              Transforme a Fachada do Seu Condomínio
            </h2>
            <p className="text-xl text-orange-50">
              Solicite uma vistoria técnica gratuita e descubra como podemos valorizar seu patrimônio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button size="lg" className="bg-white text-[#F47A20] hover:bg-gray-100 shadow-xl">
                  Solicitar Vistoria Gratuita
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="https://wa.me/5521987361748?text=Olá!%20Gostaria%20de%20orçamento%20para%20reforma%20de%20fachada." target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FacadeReform;